/**
 * MobileProjekte – Project List + Create + Edit + Quick Task
 * - ProjectSheet handles both create + edit (same mask)
 * - No "Zurück" button - bottom nav always visible
 * - pb-24 for bottom nav space
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, authFetch } from "../../App";
import { offlineAuthFetch } from "../../lib/offlineAuthFetch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "../../i18n/LanguageProvider";
import {
  MapPin, Navigation, Phone, FileText, User,
  Building2, Loader2, ChevronRight, ChevronLeft, Sparkles, ListTodo,
  Info, Plus, Camera, X, Zap, ImagePlus, Check,
  Wrench, Search, Save, Shield, Hotel, Home, ClipboardList, Star, Map,
} from "lucide-react";
import MobileTaskList from "./MobileTaskList";
import MobileBautagesbericht from "./MobileBautagesbericht";
import ProjectVaultTab from "../../components/ProjectVaultTab";

const API = "/api";

/* ── NU-Hinweise: Translation Toggle (dezent) ── */
function ProjectNotesTranslationToggle({ originalText, originalTitle, isManual }) {
  const [showOrig, setShowOrig] = useState(false);
  return (
    <div className="mt-2 pt-2 border-t border-amber-300/15 text-[10px] text-blue-300/70" data-testid="nu-notes-translation-banner">
      <div className="flex items-center justify-between gap-2">
        <span>{isManual ? "Manuell geprüfte Übersetzung" : "Automatisch übersetzt aus Deutsch"}</span>
        <button onClick={() => setShowOrig(s => !s)} className="text-blue-400 underline-offset-2 hover:underline" data-testid="nu-notes-toggle-original">
          {showOrig ? "Übersetzung" : "Original"}
        </button>
      </div>
      {showOrig && (
        <div className="mt-1.5 pt-1.5 border-t border-blue-500/15" data-testid="nu-notes-original">
          {originalTitle && <p className="text-[11px] font-semibold text-gray-300">{originalTitle}</p>}
          <p className="text-[11px] text-gray-300 whitespace-pre-wrap mt-0.5">{originalText}</p>
        </div>
      )}
    </div>
  );
}


/* ── Image Compression ── */
function compressImage(file, quality = 0.75, maxW = 1600) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) { resolve(file); return; }
    const img = new window.Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxW) { h = Math.round((h * maxW) / w); w = maxW; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((blob) => resolve(blob ? new File([blob], file.name, { type: "image/jpeg" }) : file), "image/jpeg", quality);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/* ══════════════ Photo Source Picker ══════════════ */
function PhotoPicker({ open, onClose, onCamera, onGallery }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[900] flex items-end justify-center" onClick={onClose} data-testid="photo-picker">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-md mb-safe pb-2 px-3 animate-in slide-in-from-bottom-4 duration-200" onClick={e => e.stopPropagation()}>
        <div className="bg-[#1c1f28] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <button onClick={onCamera} className="flex items-center gap-3 w-full px-4 py-4 text-left hover:bg-white/5 active:bg-white/10 transition-colors" data-testid="picker-camera">
            <div className="p-2 rounded-xl bg-blue-500/15"><Camera className="w-5 h-5 text-blue-400" /></div>
            <div><p className="text-sm font-medium text-white">Foto aufnehmen</p><p className="text-[11px] text-gray-500">Kamera oeffnen</p></div>
          </button>
          <button onClick={onGallery} className="flex items-center gap-3 w-full px-4 py-4 text-left hover:bg-white/5 active:bg-white/10 transition-colors border-t border-white/5" data-testid="picker-gallery">
            <div className="p-2 rounded-xl bg-emerald-500/15"><ImagePlus className="w-5 h-5 text-emerald-400" /></div>
            <div><p className="text-sm font-medium text-white">Aus Galerie waehlen</p><p className="text-[11px] text-gray-500">Bild auswaehlen</p></div>
          </button>
        </div>
        <button onClick={onClose} className="w-full mt-2 py-3 bg-[#1c1f28] rounded-2xl text-sm font-semibold text-gray-400 border border-white/10" data-testid="picker-cancel">Abbrechen</button>
      </div>
    </div>
  );
}

/* ══════════════ Project Sheet (Create + Edit - SAME MASK) ══════════════ */
function ProjectSheet({ open, onClose, onSaved, editProject }) {
  const isEdit = !!editProject;
  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  // Google Maps import
  const [mapsUrl, setMapsUrl] = useState("");
  const [mapsParsing, setMapsParsing] = useState(false);
  const [mapsPreview, setMapsPreview] = useState(null);
  const [mapsError, setMapsError] = useState("");
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  // Pre-fill when editing
  useEffect(() => {
    if (editProject) {
      setName(editProject.display_name || editProject.name || "");
      setShortDescription(editProject.short_description || "");
      setDescription(editProject.description || "");
      const addr = editProject.address && typeof editProject.address === "object" ? editProject.address : {};
      setStreet(addr.street || "");
      setCity(addr.city || "");
      setZip(addr.zip || "");
      setLat(addr.lat || null);
      setLng(addr.lng || null);
      setPhotoPreview(editProject.project_image || null);
      setPhoto(null);
    } else {
      setName(""); setShortDescription(""); setDescription(""); setStreet(""); setCity(""); setZip("");
      setLat(null); setLng(null); setPhoto(null); setPhotoPreview(null);
      setMapsUrl(""); setMapsPreview(null); setMapsError("");
    }
  }, [editProject, open]);

  // ─── Google Maps URL Parser ───
  const parseMapsLink = async (url) => {
    if (!url) return;
    setMapsParsing(true);
    setMapsError("");
    setMapsPreview(null);
    try {
      const res = await authFetch(`${API}/projects/parse-maps-link`, { method: "POST", body: JSON.stringify({ url }) });
      if (res.ok) {
        const data = await res.json();
        setMapsPreview(data);
      } else {
        const err = await res.json().catch(() => ({}));
        setMapsError(err.detail || "Link konnte nicht erkannt werden");
      }
    } catch { setMapsError("Fehler beim Verarbeiten"); }
    setMapsParsing(false);
  };

  const applyMapsData = () => {
    if (!mapsPreview) return;
    setName(mapsPreview.name || "");
    if (mapsPreview.address) {
      setStreet(mapsPreview.address.street || "");
      setCity(mapsPreview.address.city || "");
      setZip(mapsPreview.address.zip || "");
    }
    if (mapsPreview.lat) setLat(mapsPreview.lat);
    if (mapsPreview.lng) setLng(mapsPreview.lng);
    setMapsPreview(null);
    setMapsUrl("");
    toast.success("Daten aus Google Maps uebernommen");
  };

  const handleClipboardPaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text && (text.includes("google") || text.includes("goo.gl") || text.includes("maps"))) {
        setMapsUrl(text);
        parseMapsLink(text);
      } else {
        toast.error("Kein Google Maps Link in der Zwischenablage");
      }
    } catch {
      toast.error("Kein Zugriff auf die Zwischenablage");
    }
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setPhoto(compressed);
    setPhotoPreview(URL.createObjectURL(compressed));
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Projektname ist Pflicht"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim() };
      // Kurzbeschreibung (Listen / Karten)
      if (shortDescription.trim()) body.short_description = shortDescription.trim();
      else if (isEdit) body.short_description = "";
      // Ausführliche Beschreibung (Detail)
      if (description.trim()) body.description = description.trim();
      else if (isEdit) body.description = "";
      if (street.trim() || city.trim() || zip.trim() || lat || lng) {
        body.address = { street: street.trim(), city: city.trim(), zip: zip.trim() };
        if (lat) body.address.lat = lat;
        if (lng) body.address.lng = lng;
      }

      let projectId;
      if (isEdit) {
        const res = await authFetch(`${API}/projects/${editProject.id}`, { method: "PATCH", body: JSON.stringify(body) });
        if (!res.ok) { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler beim Speichern"); setSaving(false); return; }
        projectId = editProject.id;
      } else {
        const res = await authFetch(`${API}/projects`, { method: "POST", body: JSON.stringify(body) });
        if (!res.ok) { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler beim Erstellen"); setSaving(false); return; }
        const project = await res.json();
        projectId = project.id;
      }

      // Upload new photo if selected
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await authFetch(`${API}/projects/${projectId}/image`, { method: "POST", body: fd, headers: {} });
      }

      toast.success(isEdit ? "Projekt gespeichert" : "Projekt erstellt");
      onClose();
      onSaved(projectId);
    } catch { toast.error("Fehler"); }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[800] flex items-end justify-center" data-testid={isEdit ? "edit-project-sheet" : "create-project-sheet"}>
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#141720] rounded-t-3xl border-t border-white/10 max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom-4 duration-300" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-white/20 rounded-full" /></div>
        <div className="px-5 pb-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">{isEdit ? "Projekt bearbeiten" : "Neues Projekt"}</h2>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white rounded-lg"><X className="w-5 h-5" /></button>
          </div>

          {/* Google Maps Import (only for new projects) */}
          {!isEdit && (
            <div className="space-y-2" data-testid="maps-import-section">
              <label className="text-xs text-gray-400 font-semibold flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />Aus Google Maps importieren
              </label>
              <div className="flex gap-2">
                <input
                  value={mapsUrl}
                  onChange={(e) => setMapsUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && mapsUrl.trim()) parseMapsLink(mapsUrl); }}
                  placeholder="Google Maps Link einfuegen..."
                  className="flex-1 h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
                  data-testid="maps-url-input"
                />
                <button
                  onClick={() => mapsUrl.trim() ? parseMapsLink(mapsUrl) : handleClipboardPaste()}
                  disabled={mapsParsing}
                  className="h-11 px-4 bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-40 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 whitespace-nowrap active:scale-95 transition-all"
                  data-testid="maps-paste-btn"
                >
                  {mapsParsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  {mapsUrl.trim() ? "Laden" : "Einfuegen"}
                </button>
              </div>

              {/* Error */}
              {mapsError && (
                <p className="text-xs text-red-400 px-1" data-testid="maps-error">{mapsError}</p>
              )}

              {/* Preview Card */}
              {mapsPreview && (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 space-y-2" data-testid="maps-preview">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white truncate">{mapsPreview.name}</p>
                      {mapsPreview.address && (mapsPreview.address.street || mapsPreview.address.city) && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[mapsPreview.address.street, [mapsPreview.address.zip, mapsPreview.address.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {mapsPreview.lat && mapsPreview.lng && (
                        <p className="text-[10px] text-gray-600 mt-0.5 font-mono">{mapsPreview.lat.toFixed(5)}, {mapsPreview.lng.toFixed(5)}</p>
                      )}
                    </div>
                    <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  </div>
                  <button
                    onClick={applyMapsData}
                    className="w-full h-10 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                    data-testid="maps-apply-btn"
                  >
                    <Check className="w-4 h-4" />Daten uebernehmen
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3 px-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] text-gray-600 uppercase tracking-wider">oder manuell</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </div>
          )}

          {/* Photo */}
          <div>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoFile} />
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoFile} />
            {photoPreview ? (
              <div className="relative w-full h-40 rounded-xl overflow-hidden border border-white/10">
                <img src={photoPreview} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setShowPhotoPicker(true)}
                  className="absolute bottom-2 right-2 px-3 py-1.5 bg-black/60 rounded-lg text-[11px] text-white font-medium flex items-center gap-1"
                  data-testid="project-change-photo"
                >
                  <Camera className="w-3.5 h-3.5" />Aendern
                </button>
              </div>
            ) : (
              <button onClick={() => setShowPhotoPicker(true)}
                className="w-full h-32 border-2 border-dashed border-white/15 rounded-xl flex flex-col items-center justify-center gap-2 active:border-blue-400/40 transition-colors"
                data-testid="project-photo-btn"
              >
                <Camera className="w-6 h-6 text-gray-500" />
                <span className="text-xs text-gray-500">Baustelle fotografieren (optional)</span>
              </button>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Projektname *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Neubau Halle 5"
              className="w-full h-12 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-[15px] placeholder:text-gray-600 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/30 outline-none transition-all"
              autoFocus data-testid="project-name-input" />
          </div>

          {/* Kurzbeschreibung — schnelle Übersicht (Listen / Karten) */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Projektkurzbeschreibung <span className="text-gray-600">(optional)</span></label>
            <input value={shortDescription} onChange={e => setShortDescription(e.target.value)} maxLength={120} placeholder="z.B. Erneuerung Sicherheitsbeleuchtung"
              className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
              data-testid="project-short-desc-input" />
            <p className="text-[10px] text-gray-600 mt-1">Wird in der Projektliste unter dem Namen angezeigt · max. 120 Zeichen</p>
          </div>

          {/* Ausführliche Beschreibung — nur Detail / Info */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Ausführliche Projektbeschreibung <span className="text-gray-600">(optional)</span></label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Detaillierte Infos, Besonderheiten, Hinweise..."
              className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none resize-none transition-all"
              data-testid="project-desc-input" />
          </div>

          {/* Address */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Adresse <span className="text-gray-600">(optional)</span></label>
            <div className="space-y-2">
              <input value={street} onChange={e => setStreet(e.target.value)} placeholder="Strasse + Hausnummer"
                className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
                data-testid="project-street-input" />
              <div className="flex gap-2">
                <input value={zip} onChange={e => setZip(e.target.value)} placeholder="PLZ"
                  className="w-24 h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
                  data-testid="project-zip-input" />
                <input value={city} onChange={e => setCity(e.target.value)} placeholder="Ort"
                  className="flex-1 h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
                  data-testid="project-city-input" />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button onClick={handleSave} disabled={saving || !name.trim()}
            className="w-full h-14 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            data-testid="project-save-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : isEdit ? <Save className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {saving ? "Wird gespeichert..." : isEdit ? "Aenderungen speichern" : "Projekt erstellen"}
          </button>

          {!isEdit && <p className="text-[11px] text-gray-600 text-center">Details koennen spaeter ergaenzt werden</p>}
        </div>
      </div>
      <PhotoPicker open={showPhotoPicker} onClose={() => setShowPhotoPicker(false)}
        onCamera={() => { setShowPhotoPicker(false); setTimeout(() => cameraRef.current?.click(), 100); }}
        onGallery={() => { setShowPhotoPicker(false); setTimeout(() => galleryRef.current?.click(), 100); }} />
    </div>
  );
}


/* ══════════════ Quick Task Sheet (3-Click) ══════════════ */
function QuickTaskSheet({ open, onClose, projectId, userId, onCreated }) {
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const reset = () => { setTitle(""); setPhotoFile(null); setPhotoPreview(null); };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setPhotoFile(compressed);
    setPhotoPreview(URL.createObjectURL(compressed));
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const handleCreate = async () => {
    setCreating(true);
    const taskTitle = title.trim() || `Schnellaufgabe ${new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks`, {
        method: "POST",
        body: JSON.stringify({ title: taskTitle, status: "in_arbeit", priority: "normal", assigned_to: userId || null }),
      });
      if (!res.ok) { toast.error("Aufgabe konnte nicht erstellt werden"); setCreating(false); return; }
      const task = await res.json();
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const pRes = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}/photos`, { method: "POST", body: fd, headers: {} });
        if (pRes.ok) toast.success("Aufgabe + Foto erstellt");
        else toast.success("Aufgabe erstellt (Foto-Upload fehlgeschlagen)");
      } else { toast.success("Aufgabe erstellt"); }
      reset(); onClose(); onCreated();
    } catch { toast.error("Fehler"); }
    setCreating(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[800] flex items-end justify-center" data-testid="quick-task-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#141720] rounded-t-3xl border-t border-white/10 animate-in slide-in-from-bottom-4 duration-300" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 bg-white/20 rounded-full" /></div>
        <div className="px-5 pb-8 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" />Schnell-Aufgabe</h2>
            <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoCapture} />
          <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoCapture} />
          {photoPreview ? (
            <div className="relative w-full h-48 rounded-xl overflow-hidden border border-white/10">
              <img src={photoPreview} alt="" className="w-full h-full object-cover" />
              <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => setShowPhotoPicker(true)}
              className="w-full h-36 border-2 border-dashed border-blue-500/30 bg-blue-500/5 rounded-xl flex flex-col items-center justify-center gap-2 active:bg-blue-500/10 transition-colors"
              data-testid="quick-task-photo-btn"
            >
              <Camera className="w-8 h-8 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Foto aufnehmen</span>
              <span className="text-[10px] text-gray-600">Wird automatisch gespeichert</span>
            </button>
          )}
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Beschreibung (optional)"
            className="w-full h-12 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 outline-none transition-all"
            data-testid="quick-task-title" />
          <p className="text-[10px] text-gray-600 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-green-500" />Zeitstempel + Mitarbeiter automatisch
          </p>
          <button onClick={handleCreate} disabled={creating}
            className="w-full h-14 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            data-testid="quick-task-submit"
          >
            {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
            {creating ? "Wird erstellt..." : "Aufgabe erstellen"}
          </button>
        </div>
      </div>
      <PhotoPicker open={showPhotoPicker} onClose={() => setShowPhotoPicker(false)}
        onCamera={() => { setShowPhotoPicker(false); setTimeout(() => cameraRef.current?.click(), 100); }}
        onGallery={() => { setShowPhotoPicker(false); setTimeout(() => galleryRef.current?.click(), 100); }} />
    </div>
  );
}


/* ══════════════ Project Info Sheet (Bottom Sheet, Mobile) ══════════════ */
function ProjectInfoSheet({ project, projectManagerName, currentUserId, onClose }) {
  if (!project) return null;
  const addr = project.address && typeof project.address === "object" ? project.address : null;
  const addrStr = addr ? [addr.street, [addr.zip, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null;
  const title = project.display_name || project.name || "";
  const today = new Date().toISOString().slice(0, 10);

  // Filtere Unterkunft pro User + Datum
  const userAccommodation = (() => {
    const list = Array.isArray(project.accommodations) ? project.accommodations : [];
    if (!list.length || !currentUserId) return null;
    return list.find(a => {
      if (!a) return false;
      const users = Array.isArray(a.assigned_user_ids) ? a.assigned_user_ids : [];
      if (users.length > 0 && !users.includes(currentUserId)) return false;
      const fromOk = !a.from_date || today >= a.from_date;
      const toOk = !a.to_date || today <= a.to_date;
      return fromOk && toOk;
    }) || null;
  })();

  const isApartment = userAccommodation
    ? /ferien|wohnung|apartment|fewo/.test((userAccommodation.name || "").toLowerCase())
    : false;
  const AccIcon = isApartment ? Home : Hotel;

  const fmtDate = (d) => {
    if (!d) return "";
    try { const [y, m, day] = d.split("-"); return `${day}.${m}.${y}`; } catch { return d; }
  };

  return (
    <div className="fixed inset-0 z-[850] flex items-end justify-center" data-testid="m-project-info-sheet">
      <div className="absolute inset-0 bg-black/60 animate-in fade-in duration-200" onClick={onClose} />
      <div
        className="relative w-full max-w-md bg-[#141720] rounded-t-3xl border-t border-white/10 animate-in slide-in-from-bottom-4 duration-300 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1 sticky top-0 bg-[#141720] z-10">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Header: Titel + Kurzbeschreibung */}
        <div className="px-5 pt-2 pb-1 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white leading-tight" data-testid="m-info-name">{title}</h2>
            {project.short_description && (
              <p className="text-[13px] text-gray-400 mt-1 leading-snug" data-testid="m-info-short-desc">
                {project.short_description}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 text-gray-500 hover:text-white rounded-lg flex-shrink-0" data-testid="m-info-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status pill direkt unter Header */}
        {project.status && (
          <div className="px-5 mt-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-300">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="capitalize">{String(project.status).replace(/_/g, " ")}</span>
            </span>
          </div>
        )}

        {/* Soft Sections */}
        <div className="px-5 pt-4 pb-8 space-y-5">
          {/* 1) Einsatzort */}
          {addrStr && (
            <SoftSection
              icon={MapPin}
              iconColor="text-blue-400"
              label="Einsatzort"
              testId="m-info-address"
              onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrStr)}`, "_blank")}
            >
              <span className="text-sm text-white leading-snug">{addrStr}</span>
            </SoftSection>
          )}

          {/* 2) Projektleiter */}
          {projectManagerName && (
            <SoftSection icon={User} iconColor="text-emerald-400" label="Projektleiter" testId="m-info-manager">
              <span className="text-sm text-white leading-snug">{projectManagerName}</span>
            </SoftSection>
          )}

          {/* 3) Unterkunft (nur wenn dem User zugewiesen + Datum passt) */}
          {userAccommodation && (
            <SoftSection
              icon={AccIcon}
              iconColor={isApartment ? "text-emerald-300" : "text-blue-300"}
              label="Unterkunft"
              testId="m-info-accommodation"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-semibold">{userAccommodation.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    isApartment ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"
                  }`}>
                    {isApartment ? "Ferienwohnung" : "Hotel"}
                  </span>
                </div>
                {(userAccommodation.from_date || userAccommodation.to_date) && (
                  <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                    {fmtDate(userAccommodation.from_date) || "—"} → {fmtDate(userAccommodation.to_date) || "—"}
                  </div>
                )}
                {userAccommodation.address && (
                  <div className="text-[11px] text-gray-500 mt-0.5">{userAccommodation.address}</div>
                )}
              </div>
            </SoftSection>
          )}

          {/* 4) Ausführliche Beschreibung — nur wenn vorhanden, klein gehalten */}
          {project.description && (
            <SoftSection icon={FileText} iconColor="text-gray-400" label="Beschreibung" testId="m-info-desc">
              <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap">{project.description}</p>
            </SoftSection>
          )}
        </div>
      </div>
    </div>
  );
}

/* Soft-Section: dezentes Padding, kein harter Rahmen — Icon links, Text rechts */
function SoftSection({ icon: Icon, iconColor, label, children, onClick, testId }) {
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`w-full flex items-start gap-3 text-left ${onClick ? "active:opacity-70 transition-opacity" : ""}`}
      data-testid={testId}
    >
      <div className={`w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1">{label}</p>
        {children}
      </div>
    </Wrap>
  );
}

/* ══════════════ Project Quick-Info Tile (oben im "Info"-Tab) ══════════════
   Read-only Übersicht der wichtigsten Projekt-Infos: Einsatzort, Projektleiter, Beschreibung.
   Spiegelt das Bottom-Sheet vom Projekt-Card-"i"-Button, jedoch dauerhaft sichtbar im Info-Tab. */
function ProjectQuickInfoTile({ project, projectManagerName, currentUserId, currentUserRole, onOpenMaps }) {
  const addr = project?.address && typeof project.address === "object" ? project.address : null;
  const addrStr = addr ? [addr.street, [addr.zip, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null;
  const today = new Date().toISOString().slice(0, 10);

  const userAccommodation = (() => {
    const list = Array.isArray(project?.accommodations) ? project.accommodations : [];
    if (!list.length || !currentUserId) return null;
    return list.find(a => {
      if (!a) return false;
      const users = Array.isArray(a.assigned_user_ids) ? a.assigned_user_ids : [];
      if (users.length > 0 && !users.includes(currentUserId)) return false;
      const fromOk = !a.from_date || today >= a.from_date;
      const toOk = !a.to_date || today <= a.to_date;
      return fromOk && toOk;
    }) || null;
  })();
  const isApartment = userAccommodation
    ? /ferien|wohnung|apartment|fewo/.test((userAccommodation.name || "").toLowerCase())
    : false;
  const AccIcon = isApartment ? Home : Hotel;

  const fmtDate = (d) => {
    if (!d) return "";
    try { const [y, m, day] = d.split("-"); return `${day}.${m}.${y}`; } catch { return d; }
  };

  // Nichts anzeigen, wenn keine Daten vorhanden sind
  if (!addrStr && !projectManagerName && !userAccommodation && !project?.description) return null;

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 space-y-4"
      data-testid="m-info-quick-tile"
    >
      <div className="flex items-center gap-2 -mb-1">
        <div className="w-6 h-6 rounded-md bg-blue-500/15 flex items-center justify-center">
          <Info className="w-3.5 h-3.5 text-blue-300" />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-blue-300 font-bold">Übersicht</p>
      </div>

      {addrStr && (
        <SoftSection
          icon={MapPin}
          iconColor="text-blue-400"
          label="Einsatzort"
          testId="m-info-quick-address"
          onClick={onOpenMaps}
        >
          <span className="text-sm text-white leading-snug">{addrStr}</span>
        </SoftSection>
      )}

      {projectManagerName && (
        <SoftSection icon={User} iconColor="text-emerald-400" label="Projektleiter" testId="m-info-quick-manager">
          <span className="text-sm text-white leading-snug">{projectManagerName}</span>
        </SoftSection>
      )}

      {userAccommodation && (
        <SoftSection
          icon={AccIcon}
          iconColor={isApartment ? "text-emerald-300" : "text-blue-300"}
          label="Unterkunft"
          testId="m-info-quick-accommodation"
        >
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white font-semibold">{userAccommodation.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                isApartment ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"
              }`}>
                {isApartment ? "Ferienwohnung" : "Hotel"}
              </span>
            </div>
            {(userAccommodation.from_date || userAccommodation.to_date) && (
              <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                {fmtDate(userAccommodation.from_date) || "—"} → {fmtDate(userAccommodation.to_date) || "—"}
              </div>
            )}
            {userAccommodation.booking_number && (
              <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                Buchungsnummer: {userAccommodation.booking_number}
              </div>
            )}
          </div>
        </SoftSection>
      )}

      {project?.description && (
        <SoftSection icon={FileText} iconColor="text-gray-400" label="Beschreibung" testId="m-info-quick-desc">
          <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap">{project.description}</p>
        </SoftSection>
      )}

      {/* Hinweise für Nachunternehmer */}
      {(() => {
        const notes = (project?.subcontractor_notes || "").trim();
        if (!notes) return null;
        const role = (currentUserRole || "").toLowerCase();
        const isPrivileged = role === "admin" || role === "projektleiter" || role === "geschaeftsfuehrung";
        const visible = project.subcontractor_notes_visible !== false;
        if (!isPrivileged && !visible) return null;
        // Localized (für SK/EN-User) bevorzugen
        const displayTitle = (project.subcontractor_notes_title_localized || project.subcontractor_notes_title || "").trim() || "Hinweise für Nachunternehmer";
        const displayNotes = (project.subcontractor_notes_localized || project.subcontractor_notes || "").trim();
        const isTranslated = project.subcontractor_notes_translation_status === "auto" || project.subcontractor_notes_translation_status === "manual_reviewed";
        const isManual = project.subcontractor_notes_translation_reviewed;
        return (
          <SoftSection
            icon={ClipboardList}
            iconColor="text-amber-300"
            label={displayTitle}
            testId="m-info-quick-nu-notes"
          >
            <p className="text-[13px] text-gray-100 leading-relaxed whitespace-pre-wrap select-text" data-testid="nu-notes-text-quick">{displayNotes}</p>
            {isTranslated && (
              <ProjectNotesTranslationToggle
                originalText={project.subcontractor_notes}
                originalTitle={project.subcontractor_notes_title}
                isManual={isManual}
              />
            )}
            {isPrivileged && !visible && (
              <p className="mt-2 text-[10px] uppercase tracking-wider text-amber-300/70">
                Hinweis: aktuell für Nachunternehmer ausgeblendet
              </p>
            )}
          </SoftSection>
        );
      })()}
    </div>
  );
}


/* ══════════════ Mobile Project Card (Image-as-Background, modern) ══════════════ */
/* ══════════════ Project Inline Info ══════════════
   Erscheint direkt anstelle der Projekt-Card, wenn der User auf das "i"-Icon tippt.
   Zeigt: Einsatzort, Projektleiter, Unterkunft (falls vorhanden), Beschreibung
   + großer "Zurück"-Button oben und ein "Projekt öffnen"-Button am Ende. */
function ProjectInlineInfo({ project, addrShort, projectManagerName, currentUserId, currentUserRole, myAcc, accIsApartment, AccIcon, onClose, onOpenProject }) {
  const title = project.display_name || project.name || "";
  const status = project.status || "active";
  const statusLabel = ({ active: "Aktiv", completed: "Abgeschlossen", on_hold: "Pausiert", planning: "Planung" })[status] || status;
  const statusDot = ({ active: "bg-emerald-500", completed: "bg-blue-500", on_hold: "bg-amber-500", planning: "bg-gray-500" })[status] || "bg-gray-500";

  const fmtDate = (d) => {
    if (!d) return "";
    try { const [y, m, day] = d.split("-"); return `${day}.${m}.${y}`; } catch { return d; }
  };

  const addr = project.address && typeof project.address === "object" ? project.address : null;
  const addrFull = addr ? [addr.street, [addr.zip, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : addrShort;

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{
        background: "rgba(15, 20, 30, 0.78)",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.40), inset 0 0 0 1px rgba(255,255,255,0.10)",
      }}
      data-testid={`m-project-inline-info-${project.id}`}
    >
      {/* Header mit Zurück-Button und Titel */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/[0.08] hover:bg-white/[0.14] active:scale-90 flex items-center justify-center flex-shrink-0 transition-all"
          aria-label="Zurück zur Projekt-Karte"
          data-testid={`m-info-back-${project.id}`}
        >
          <ChevronLeft className="w-5 h-5 text-white" strokeWidth={2.4} />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="text-[18px] font-bold leading-tight tracking-tight text-white truncate">
            {title}
          </h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
            <span className="text-[11px] text-gray-400 font-medium">{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Info-Sektionen */}
      <div className="px-4 pb-4 space-y-4">
        {addrFull && (
          <SoftSection
            icon={MapPin}
            iconColor="text-blue-400"
            label="Einsatzort"
            testId={`m-info-address-${project.id}`}
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrFull)}`, "_blank")}
          >
            <span className="text-sm text-white leading-snug">{addrFull}</span>
          </SoftSection>
        )}

        {projectManagerName && (
          <SoftSection icon={User} iconColor="text-emerald-400" label="Projektleiter" testId={`m-info-manager-${project.id}`}>
            <span className="text-sm text-white leading-snug">{projectManagerName}</span>
          </SoftSection>
        )}

        {myAcc && (
          <SoftSection
            icon={AccIcon}
            iconColor={accIsApartment ? "text-emerald-300" : "text-blue-300"}
            label="Unterkunft"
            testId={`m-info-accommodation-${project.id}`}
          >
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-white font-semibold">{myAcc.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  accIsApartment ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"
                }`}>
                  {accIsApartment ? "Ferienwohnung" : "Hotel"}
                </span>
              </div>
              {(myAcc.from_date || myAcc.to_date) && (
                <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                  {fmtDate(myAcc.from_date) || "—"} → {fmtDate(myAcc.to_date) || "—"}
                </div>
              )}
              {myAcc.booking_number && (
                <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                  Buchungsnummer: {myAcc.booking_number}
                </div>
              )}
            </div>
          </SoftSection>
        )}

        {project.description && (
          <SoftSection icon={FileText} iconColor="text-gray-400" label="Beschreibung" testId={`m-info-desc-${project.id}`}>
            <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap">{project.description}</p>
          </SoftSection>
        )}

        {/* Hinweise für Nachunternehmer — nur anzeigen, wenn Text vorhanden UND (Admin/PL ODER visible=true) */}
        {(() => {
          const notes = (project.subcontractor_notes || "").trim();
          if (!notes) return null;
          const role = (currentUserRole || "").toLowerCase();
          const isPrivileged = role === "admin" || role === "projektleiter" || role === "geschaeftsfuehrung";
          const visible = project.subcontractor_notes_visible !== false; // Default: true wenn Text vorhanden
          if (!isPrivileged && !visible) return null;
          // Localized (für SK/EN-User) bevorzugen — Backend liefert *_localized
          const displayTitle = (project.subcontractor_notes_title_localized || project.subcontractor_notes_title || "").trim() || "Hinweise für Nachunternehmer";
          const displayNotes = (project.subcontractor_notes_localized || project.subcontractor_notes || "").trim();
          const isTranslated = project.subcontractor_notes_translation_status === "auto" || project.subcontractor_notes_translation_status === "manual_reviewed";
          const isManual = project.subcontractor_notes_translation_reviewed;
          return (
            <SoftSection
              icon={ClipboardList}
              iconColor="text-amber-300"
              label={displayTitle}
              testId={`m-info-nu-notes-${project.id}`}
            >
              <p className="text-[13px] text-gray-100 leading-relaxed whitespace-pre-wrap select-text" data-testid="nu-notes-text">{displayNotes}</p>
              {isTranslated && (
                <ProjectNotesTranslationToggle
                  originalText={project.subcontractor_notes}
                  originalTitle={project.subcontractor_notes_title}
                  isManual={isManual}
                />
              )}
              {isPrivileged && !visible && (
                <p className="mt-2 text-[10px] uppercase tracking-wider text-amber-300/70">
                  Hinweis: aktuell für Nachunternehmer ausgeblendet
                </p>
              )}
            </SoftSection>
          );
        })()}

        {/* CTAs: Projekt öffnen + Zurück */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-xl bg-white/[0.05] hover:bg-white/[0.10] active:scale-95 text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-all border border-white/10"
            data-testid={`m-info-close-${project.id}`}
          >
            <ChevronLeft className="w-4 h-4" />
            Zurück
          </button>
          <button
            type="button"
            onClick={onOpenProject}
            className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-95 text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-all shadow-lg shadow-blue-600/30"
            data-testid={`m-info-open-${project.id}`}
          >
            Projekt öffnen
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectCard({ project, addrShort, onOpen, onInfo, isInfoOpen, onCloseInfo, projectManagerName, currentUserId, currentUserRole, relevance, isFavorite, onToggleFavorite }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = !!project.project_image && !imgError;
  const title = project.display_name || project.name || "";
  const rel = relevance || {};

  // Hotel/Ferienwohnung-Icon als Schnellinfo: User hat aktuelle ODER künftige Unterkunft im Projekt
  const todayIso = new Date().toISOString().slice(0, 10);
  const myAcc = (() => {
    if (!currentUserId) return null;
    const list = Array.isArray(project.accommodations) ? project.accommodations : [];
    // Nur Unterkünfte, die dem User zugewiesen sind UND deren to_date >= heute (oder kein to_date)
    const mine = list.filter(a => {
      if (!a) return false;
      const users = Array.isArray(a.assigned_user_ids) ? a.assigned_user_ids : [];
      if (!users.includes(currentUserId)) return false;
      // Vergangene Unterkünfte ausblenden
      if (a.to_date && a.to_date < todayIso) return false;
      return true;
    });
    if (mine.length === 0) return null;
    // Bevorzugt: aktiv (heute innerhalb Zeitraum); sonst nächste in Zukunft (kleinstes from_date)
    const active = mine.find(a => {
      const fromOk = !a.from_date || todayIso >= a.from_date;
      const toOk = !a.to_date || todayIso <= a.to_date;
      return fromOk && toOk;
    });
    if (active) return { ...active, _state: "active" };
    const upcoming = [...mine].sort((a, b) => (a.from_date || "9999").localeCompare(b.from_date || "9999"))[0];
    return upcoming ? { ...upcoming, _state: "upcoming" } : null;
  })();
  const accIsApartment = myAcc ? /ferien|wohnung|apartment|fewo/.test((myAcc.name || "").toLowerCase()) : false;
  const AccIcon = accIsApartment ? Home : Hotel;

  // Bild-URL absolut machen, falls relativ (verhindert Probleme mit Service-Worker / Caching)
  const imgSrc = (() => {
    if (!project.project_image) return null;
    const u = project.project_image;
    if (u.startsWith("http")) return u;
    if (u.startsWith("/")) return `${window.location.origin}${u}`;
    return u;
  })();

  // ── Inline-Info-View: erscheint direkt anstelle der Card, wenn der User auf "i" tippt ──
  if (isInfoOpen) {
    return (
      <ProjectInlineInfo
        project={project}
        addrShort={addrShort}
        projectManagerName={projectManagerName}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        myAcc={myAcc}
        accIsApartment={accIsApartment}
        AccIcon={AccIcon}
        onClose={onCloseInfo}
        onOpenProject={onOpen}
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="relative w-full h-[180px] rounded-2xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      data-testid={`m-project-${project.id}`}
      style={{
        background: hasImage ? "var(--tga-card-bg)" : "var(--tga-card-bg-empty)",
        backdropFilter: hasImage ? undefined : "blur(20px) saturate(180%)",
        WebkitBackdropFilter: hasImage ? undefined : "blur(20px) saturate(180%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35), inset 0 0 0 1px var(--tga-card-border)",
      }}
    >
      {/* Bildebene */}
      {hasImage ? (
        <img
          src={imgSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            // Brillanter, kontrastreicher Look (CSS-Filter)
            filter: "contrast(1.15) saturate(1.2) brightness(1.05)",
            transform: "scale(1.01)", // verhindert dünne Border-Linien an den Rändern bei Filter
          }}
          onError={() => setImgError(true)}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-start pl-5 opacity-25">
          <Building2 className="w-20 h-20 text-blue-300" strokeWidth={1.4} />
        </div>
      )}

      {/* Premium-Overlay für Tiefe + Lesbarkeit */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: hasImage
            ? "linear-gradient(to top, var(--tga-card-overlay-start) 0%, var(--tga-card-overlay-mid) 40%, rgba(0,0,0,0.10) 70%, transparent 100%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 60%, var(--tga-card-overlay-empty-end) 100%)",
        }}
      />

      {/* Info-Icon oben rechts — hoher Kontrast, gut sichtbar auf jedem Bild */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onInfo(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.stopPropagation(); }}
        className="absolute top-2.5 right-2.5 w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
        style={{
          background: "rgba(15,20,30,0.78)",
          border: "1.5px solid rgba(255,255,255,0.55)",
          color: "#FFFFFF",
          boxShadow: "0 4px 14px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.25)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
        aria-label="Projekt-Info anzeigen"
        data-testid={`m-project-info-btn-${project.id}`}
      >
        <Info className="w-5 h-5" strokeWidth={2.6} />
      </button>

      {/* Hotel/Ferienwohnung-Schnellinfo: User hat aktuelle oder künftige Unterkunft */}
      {myAcc && (() => {
        const isActive = myAcc._state === "active";
        const palette = accIsApartment
          ? { bg: isActive ? "rgba(16,185,129,0.92)" : "rgba(16,185,129,0.78)", border: "rgba(255,255,255,0.55)", text: "#FFFFFF" }
          : { bg: isActive ? "rgba(37,99,235,0.92)" : "rgba(37,99,235,0.78)", border: "rgba(255,255,255,0.55)", text: "#FFFFFF" };
        const label = accIsApartment ? "FeWo" : "Hotel";
        return (
          <div
            className="absolute top-2.5 right-[3.5rem] flex items-center gap-1.5 px-2.5 h-10 rounded-full text-[12px] font-bold"
            style={{
              background: palette.bg,
              border: `1.5px solid ${palette.border}`,
              color: palette.text,
              boxShadow: "0 4px 14px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.25)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
            data-testid={`m-project-acc-badge-${project.id}`}
            title={`${isActive ? "Heute Unterkunft" : "Bald Unterkunft"}: ${myAcc.name}`}
          >
            <AccIcon className="w-4 h-4" strokeWidth={2.6} />
            <span>{label}</span>
            {!isActive && (
              <span
                className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold leading-none"
                style={{ background: "rgba(255,255,255,0.28)", color: "#FFFFFF" }}
              >
                BALD
              </span>
            )}
          </div>
        );
      })()}

      {/* Star-Icon oben links (Favoriten) — bewusst links platziert, damit es nicht mit dem Info-Icon oben rechts überlappt */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(project.id); }}
        className="absolute top-2.5 left-2.5 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
        style={{
          background: "rgba(15,20,30,0.78)",
          border: "1.5px solid rgba(255,255,255,0.55)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.25)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
        data-testid={`m-project-fav-${project.id}`}
        aria-label={isFavorite ? "Favorit entfernen" : "Als Favorit markieren"}
      >
        <Star className={`w-5 h-5 ${isFavorite ? "text-amber-300 fill-amber-300" : "text-white/85"}`} strokeWidth={2.4} />
      </button>

      {/* Text-Block unten links */}
      <div className="absolute left-0 right-0 bottom-0 px-4 pt-6 pb-3.5 pr-14">
        {(rel.assigned_today || rel.assigned_tomorrow || (rel.open_tasks || 0) > 0) && (
          <div className="flex gap-1.5 flex-wrap mb-1.5" data-testid={`m-project-badges-${project.id}`}>
            {rel.assigned_today && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-200 text-[10px] font-semibold border border-emerald-400/40" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                Heute eingeplant
              </span>
            )}
            {!rel.assigned_today && rel.assigned_tomorrow && (
              <span className="px-2 py-0.5 rounded-full bg-blue-500/30 text-blue-200 text-[10px] font-semibold border border-blue-400/40">
                Morgen
              </span>
            )}
            {(rel.open_tasks || 0) > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-100 text-[10px] font-semibold border border-amber-400/40">
                {rel.open_tasks} offen
              </span>
            )}
          </div>
        )}
        <h3
          className="text-[18px] leading-tight tracking-tight truncate"
          style={{
            color: "#FFFFFF",
            fontWeight: 700,
            textShadow: "0 2px 8px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5)",
          }}
          data-testid={`m-project-name-${project.id}`}
        >
          {title}
        </h3>
        {project.short_description ? (
          <p
            className="text-[12.5px] mt-1 leading-snug"
            style={{
              color: "#9AA4B2",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textShadow: "0 1px 2px rgba(0,0,0,0.4)",
            }}
            data-testid={`m-project-short-desc-${project.id}`}
          >
            {project.short_description}
          </p>
        ) : null}
        {addrShort && (
          <p
            className="flex items-center gap-1 text-[11.5px] mt-1.5 truncate"
            style={{ color: "#7c8694", textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}
          >
            <MapPin className="w-3 h-3 flex-shrink-0 opacity-80" />
            <span className="truncate">{addrShort}</span>
          </p>
        )}
      </div>
    </div>
  );
}

/* ══════════════ MAIN: Project List ══════════════ */
export default function MobileProjekte() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showBtb, setShowBtb] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [infoProject, setInfoProject] = useState(null);
  const [filterMode, setFilterMode] = useState(() => localStorage.getItem("m-proj-filter") || "all");
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("m-proj-sort") || "relevance");
  const [showSort, setShowSort] = useState(false);
  const [relevance, setRelevance] = useState({});
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  const loadRelevance = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/me/projects-relevance`);
      if (res.ok) {
        const d = await res.json();
        setRelevance(d.relevance || {});
        setFavoriteIds(new Set(d.favorite_ids || []));
      }
    } catch {}
  }, []);

  const toggleFavorite = async (projectId) => {
    const res = await authFetch(`${API}/me/favorites/projects`, {
      method: "POST",
      body: JSON.stringify({ project_id: projectId }),
    });
    if (res.ok) {
      const d = await res.json();
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (d.favorited) next.add(projectId); else next.delete(projectId);
        return next;
      });
    }
  };

  useEffect(() => { localStorage.setItem("m-proj-filter", filterMode); }, [filterMode]);
  useEffect(() => { localStorage.setItem("m-proj-sort", sortMode); }, [sortMode]);

  const loadProjects = useCallback(async () => {
    try {
      const res = await offlineAuthFetch(`${API}/projects?current_only=true`, {}, { cacheKey: "projects-list-current" });
      if (res.ok) {
        const data = await res.json();
        const active = data.filter((p) => p.is_active !== false && p.status === "aktiv");
        setProjects(active);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadProjects(); loadRelevance(); }, [loadProjects, loadRelevance]);

  // Mitarbeiter-Liste für Projektleiter-Anzeige im Info-Sheet
  useEffect(() => {
    (async () => {
      try {
        const res = await offlineAuthFetch(`${API}/users`, {}, { cacheKey: "users-list-mobile" });
        if (res.ok) setUsers(await res.json());
      } catch {}
    })();
  }, []);

  const projectManagerName = (p) => {
    if (!p?.project_manager) return p?.project_manager_name || "";
    const u = users.find(x => x.id === p.project_manager);
    return u?.name || p.project_manager_name || "";
  };

  const buildMapsUrl = (addr) => {
    const parts = [addr?.street, addr?.zip, addr?.city].filter(Boolean);
    if (parts.length === 0) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(", "))}`;
  };

  const openMaps = (addr) => { const url = buildMapsUrl(addr); if (url) window.open(url, "_blank"); };
  const callPhone = (phone) => { if (phone) window.open(`tel:${phone}`, "_self"); };

  const selected = projects.find((p) => p.id === selectedId);

  // ── BTB View (renders inline, bottom nav stays) ──
  if (selected && showBtb) {
    return (
      <MobileBautagesbericht
        projectId={selected.id}
        projectName={selected.display_name || selected.name}
        onBack={() => setShowBtb(false)}
      />
    );
  }

  // ── Project Detail View ──
  if (selected) {
    return (
      <MobileProjectDetail
        project={selected}
        onBack={() => setSelectedId(null)}
        navigate={navigate}
        buildMapsUrl={buildMapsUrl}
        openMaps={openMaps}
        callPhone={callPhone}
        onShowBtb={() => setShowBtb(true)}
        user={user}
        projectManagerName={projectManagerName(selected)}
        onProjectUpdated={(updated) => {
          setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
        }}
        loadProjects={loadProjects}
      />
    );
  }

  // ── Filter + Sort Logic ──
  const q = search.toLowerCase();
  const role = (user?.role || "").toLowerCase();
  const uid = user?.id;
  const getRel = (pid) => relevance[pid] || {};

  // 1) Search filter
  let filtered = q
    ? projects.filter(p => {
        const name = (p.display_name || p.name || "").toLowerCase();
        const addr = p.address && typeof p.address === "object"
          ? [p.address.street || "", p.address.city || "", p.address.zip || ""].join(" ").toLowerCase() : "";
        return name.includes(q) || addr.includes(q);
      })
    : projects;

  // 2) Filter-Mode
  filtered = filtered.filter(p => {
    const r = getRel(p.id);
    if (filterMode === "today") return r.assigned_today;
    if (filterMode === "week") return r.assigned_this_week || r.assigned_today || r.assigned_tomorrow;
    if (filterMode === "mine") return r.is_team_member || p.project_manager === uid;
    if (filterMode === "open_tasks") return (r.open_tasks || 0) > 0;
    if (filterMode === "favorites") return favoriteIds.has(p.id);
    return true; // "all"
  });

  // 3) Sort
  const relScore = (p) => {
    const r = getRel(p.id);
    let score = 0;
    if (favoriteIds.has(p.id) && r.assigned_today) score += 10000;
    if (r.assigned_today) score += 5000;
    if (favoriteIds.has(p.id)) score += 2500;
    if (r.assigned_tomorrow) score += 1500;
    if (r.assigned_this_week) score += 800;
    if ((r.open_tasks || 0) > 0) score += 200 + Math.min(r.open_tasks * 5, 100);
    if (r.is_team_member) score += 50;
    return score;
  };
  filtered = [...filtered].sort((a, b) => {
    if (sortMode === "relevance") {
      const diff = relScore(b) - relScore(a);
      if (diff !== 0) return diff;
      return (a.name || "").localeCompare(b.name || "");
    }
    if (sortMode === "name") return (a.name || "").localeCompare(b.name || "");
    if (sortMode === "open_tasks") return (getRel(b.id).open_tasks || 0) - (getRel(a.id).open_tasks || 0);
    if (sortMode === "start") return (a.start_date || "").localeCompare(b.start_date || "");
    if (sortMode === "assignment") {
      const aT = getRel(a.id).assigned_today ? 0 : getRel(a.id).assigned_tomorrow ? 1 : getRel(a.id).assigned_this_week ? 2 : 3;
      const bT = getRel(b.id).assigned_today ? 0 : getRel(b.id).assigned_tomorrow ? 1 : getRel(b.id).assigned_this_week ? 2 : 3;
      return aT - bT;
    }
    return 0;
  });

  // Status-Counts für Chip-Badges
  const todayCount = projects.filter(p => getRel(p.id).assigned_today).length;
  const weekCount = projects.filter(p => { const r = getRel(p.id); return r.assigned_this_week || r.assigned_today; }).length;
  const mineCount = projects.filter(p => { const r = getRel(p.id); return r.is_team_member || p.project_manager === uid; }).length;
  const openCount = projects.filter(p => (getRel(p.id).open_tasks || 0) > 0).length;
  const favCount = projects.filter(p => favoriteIds.has(p.id)).length;


  return (
    <div className="bg-[#0f1117] text-white min-h-full" data-testid="m-projekte">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-lg font-bold text-white">{t("mp.title", "Meine Projekte")}</h1>
        {!loading && projects.length > 0 && <p className="text-xs text-gray-500 mt-0.5">{t("mp.active", { count: projects.length }, `${projects.length} aktiv`)}</p>}
      </div>

      {/* Search */}
      {!loading && projects.length > 3 && (
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t("mp.search", "Projekt suchen…")}
              className="w-full h-11 pl-10 pr-9 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 outline-none transition-all"
              data-testid="m-project-search" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-500 hover:text-white" data-testid="m-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* ── Filter Chips ── */}
          <div className="flex gap-1.5 mt-2.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="m-proj-filter-chips">
            {[
              { id: "all", label: t("mp.filter_all", "Alle"), count: projects.length },
              { id: "today", label: t("mp.filter_today", "Heute"), count: todayCount },
              { id: "week", label: t("mp.filter_week", "Woche"), count: weekCount },
              { id: "mine", label: t("mp.filter_mine", "Meine"), count: mineCount },
              { id: "open_tasks", label: t("mp.filter_open", "Offen"), count: openCount },
              { id: "favorites", label: "★", count: favCount },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterMode(f.id)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${filterMode === f.id ? "bg-blue-500/25 text-blue-200 border border-blue-400/40" : "bg-white/[0.06] text-gray-400 border border-white/10 active:bg-white/[0.10]"}`}
                data-testid={`m-proj-filter-${f.id}`}
              >
                {f.label} {f.count > 0 && <span className="opacity-70">({f.count})</span>}
              </button>
            ))}
            <button onClick={() => setShowSort(true)} className="ml-auto px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap bg-white/[0.06] text-gray-300 border border-white/10 active:bg-white/[0.10]" data-testid="m-proj-sort-btn">
              Sortieren
            </button>
          </div>
        </div>
      )}

      {/* Sort Sheet */}
      {showSort && (
        <div className="fixed inset-0 z-[800] flex items-end justify-center bg-black/60" onClick={() => setShowSort(false)} data-testid="m-proj-sort-sheet">
          <div className="w-full max-w-md bg-[#1c1f28] border border-white/10 rounded-t-2xl p-3" onClick={e => e.stopPropagation()} style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
            <p className="text-xs uppercase text-gray-500 px-3 py-2">Sortieren nach</p>
            {[
              { id: "relevance", label: "Heute relevant" },
              { id: "assignment", label: "Einsatzplanung" },
              { id: "open_tasks", label: "Offene Aufgaben" },
              { id: "name", label: "Projektname A–Z" },
              { id: "start", label: "Projektstart" },
            ].map(o => (
              <button key={o.id} onClick={() => { setSortMode(o.id); setShowSort(false); }}
                className={`w-full text-left px-3 py-3 rounded-lg text-sm ${sortMode === o.id ? "bg-blue-500/15 text-blue-200" : "text-gray-200 active:bg-white/5"}`}
                data-testid={`m-proj-sort-${o.id}`}
              >{o.label}{sortMode === o.id && " ✓"}</button>
            ))}
          </div>
        </div>
      )}

      {/* Fake-Container für Search-Wrapper Schließen */}
      {!loading && projects.length > 3 && (
        <div className="hidden">
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-gray-500 animate-spin" /></div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <Building2 className="w-14 h-14 text-gray-600 mb-3" />
          <p className="text-sm text-gray-500 text-center mb-4">{t("mp.no_projects", "Keine aktiven Projekte")}</p>
          {user?.role === "admin" && (
            <button onClick={() => setShowCreate(true)} className="px-5 py-3 bg-blue-600 rounded-xl text-sm font-semibold text-white flex items-center gap-2" data-testid="m-create-first-project">
              <Plus className="w-4 h-4" />Erstes Projekt anlegen
            </button>
          )}
        </div>
      ) : (
        <div className="px-4 space-y-4 pb-24">
          {search && filtered.length === 0 && (
            <div className="text-center py-10">
              <Search className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Kein Projekt gefunden</p>
              <p className="text-xs text-gray-600 mt-1">"{search}"</p>
            </div>
          )}
          {filtered.map((p) => {
            const addr = p.address && typeof p.address === "object" ? p.address : null;
            const addrShort = addr ? [addr.street, addr.city].filter(Boolean).join(", ") : null;
            const isInfoOpen = infoProject?.id === p.id;
            return (
              <ProjectCard
                key={p.id}
                project={p}
                addrShort={addrShort}
                onOpen={() => setSelectedId(p.id)}
                onInfo={() => setInfoProject(p)}
                isInfoOpen={isInfoOpen}
                onCloseInfo={() => setInfoProject(null)}
                projectManagerName={projectManagerName(p)}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                relevance={getRel(p.id)}
                isFavorite={favoriteIds.has(p.id)}
                onToggleFavorite={toggleFavorite}
              />
            );
          })}
        </div>
      )}

      {/* Floating Create Button */}
      {user?.role === "admin" && (
        <button onClick={() => setShowCreate(true)}
          className="fixed bottom-24 right-5 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-500 rounded-full shadow-lg shadow-blue-600/30 flex items-center justify-center active:scale-90 transition-all"
          data-testid="m-fab-create-project"
        >
          <Plus className="w-6 h-6 text-white" />
        </button>
      )}

      <ProjectSheet open={showCreate} onClose={() => setShowCreate(false)}
        onSaved={(id) => { setShowCreate(false); loadProjects().then(() => setSelectedId(id)); }} />

    </div>
  );
}


/* ══════════════ Project Detail – Inline Editing ══════════════ */
function MobileProjectDetail({ project, onBack, navigate, buildMapsUrl, openMaps, callPhone, onShowBtb, user, projectManagerName, onProjectUpdated, loadProjects }) {
  const [activeTab, setActiveTab] = useState("info");
  const [taskCount, setTaskCount] = useState(0);
  const [reportCount, setReportCount] = useState(0);
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [taskListKey, setTaskListKey] = useState(0);

  // ─── Inline Edit State ───
  const [editingName, setEditingName] = useState(false);
  const [localName, setLocalName] = useState(project.display_name || project.name || "");
  const [localDesc, setLocalDesc] = useState(project.description || "");
  const [localStreet, setLocalStreet] = useState(project.address?.street || "");
  const [localCity, setLocalCity] = useState(project.address?.city || "");
  const [localZip, setLocalZip] = useState(project.address?.zip || "");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const autoSaveRef = useRef(null);
  const pendingRef = useRef(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const nameInputRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const addr = project.address && typeof project.address === "object" ? project.address : null;
  const addrText = addr ? [addr.street, [addr.zip, addr.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null;
  const mapsUrl = buildMapsUrl(addr);
  const projectTitle = project.display_name || project.name;

  // Sync when project changes
  useEffect(() => {
    setLocalName(project.display_name || project.name || "");
    setLocalDesc(project.description || "");
    setLocalStreet(project.address?.street || "");
    setLocalCity(project.address?.city || "");
    setLocalZip(project.address?.zip || "");
  }, [project.id, project.display_name, project.name, project.description, project.address]);

  // ─── Auto Save ───
  const patchProject = useCallback(async (fields) => {
    setSaveStatus("saving");
    try {
      const res = await authFetch(`${API}/projects/${project.id}`, { method: "PATCH", body: JSON.stringify(fields) });
      if (res.ok) {
        const updated = await res.json();
        onProjectUpdated(updated);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else { setSaveStatus("error"); toast.error("Fehler beim Speichern"); setTimeout(() => setSaveStatus("idle"), 3000); }
    } catch { setSaveStatus("error"); setTimeout(() => setSaveStatus("idle"), 3000); }
  }, [project.id, onProjectUpdated]);

  const saveDebounced = useCallback((fields) => {
    pendingRef.current = { ...(pendingRef.current || {}), ...fields };
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      if (pendingRef.current) { patchProject(pendingRef.current); pendingRef.current = null; }
    }, 600);
  }, [patchProject]);

  useEffect(() => () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); }, []);

  // ─── Photo Upload ───
  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file);
    setSaveStatus("saving");
    try {
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await authFetch(`${API}/projects/${project.id}/image`, { method: "POST", body: fd, headers: {} });
      if (res.ok) {
        await loadProjects();
        setSaveStatus("saved");
        toast.success("Projektbild aktualisiert");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } else { setSaveStatus("error"); toast.error("Upload fehlgeschlagen"); setTimeout(() => setSaveStatus("idle"), 3000); }
    } catch { setSaveStatus("error"); toast.error("Upload fehlgeschlagen"); setTimeout(() => setSaveStatus("idle"), 3000); }
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  // ─── Counts ───
  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, rRes] = await Promise.all([
          offlineAuthFetch(`${API}/projects/${project.id}/tasks-summary`, {}, { cacheKey: `ts-${project.id}` }).catch(() => null),
          offlineAuthFetch(`${API}/projects/${project.id}/daily-reports`, {}, { cacheKey: `dr-${project.id}` }).catch(() => null),
        ]);
        if (tRes?.ok) { const d = await tRes.json(); setTaskCount((d.by_status?.offen || 0) + (d.by_status?.in_arbeit || 0)); }
        if (rRes?.ok) { const d = await rRes.json(); setReportCount(d.length || 0); }
      } catch {}
    };
    load();
  }, [project.id, taskListKey]);

  const TABS = [
    { id: "info", label: "Info", icon: Info, count: 0 },
    { id: "aufgaben", label: "Aufgaben", icon: ListTodo, count: taskCount },
    { id: "berichte", label: "Berichte", icon: Sparkles, count: reportCount },
    ...(isAdmin ? [{ id: "tresor", label: "Tresor", icon: Shield, count: 0 }] : []),
  ];

  return (
    <div className="bg-[#0f1117] text-white min-h-full pb-4" data-testid="m-project-detail">
      {/* Hidden file inputs */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoFile} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoFile} />

      {/* ── Sticky Top Back-Bar ── */}
      <div className="sticky top-0 z-30 bg-[#0f1117]/95 backdrop-blur border-b border-white/10 px-3 py-2 flex items-center gap-2" data-testid="m-project-back-bar">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] active:scale-95 transition-all text-sm text-gray-200"
          data-testid="m-project-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="font-medium">Projekte</span>
        </button>
        <div className="flex-1 min-w-0 text-center">
          <span className="text-xs text-gray-500 truncate block">{localName || projectTitle}</span>
        </div>
        <div className="w-[80px]" /> {/* Spacer für Symmetrie */}
      </div>

      {/* ── Header ── */}
      <div className="px-4 pt-3 pb-2 flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {editingName && isAdmin ? (
            <input
              ref={nameInputRef}
              value={localName}
              onChange={(e) => { setLocalName(e.target.value); saveDebounced({ name: e.target.value }); }}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              className="text-xl font-bold text-white bg-white/[0.08] border border-blue-500/50 ring-1 ring-blue-500/20 rounded-lg px-2 py-1 w-full outline-none"
              autoFocus
              data-testid="m-project-name-input"
            />
          ) : (
            <h1
              className={`text-xl font-bold text-white leading-tight truncate ${isAdmin ? "cursor-pointer active:bg-white/[0.06] rounded-lg px-2 py-1 -mx-2 transition-colors" : ""}`}
              onClick={() => { if (isAdmin) { setEditingName(true); } }}
              data-testid="m-project-title"
            >
              {localName || projectTitle}
            </h1>
          )}
          {project.short_description && (
            <p className="text-[13px] text-gray-400 mt-1 leading-snug" data-testid="m-project-short-desc">
              {project.short_description}
            </p>
          )}
          {addrText && (
            <button onClick={() => openMaps(addr)} className="flex items-center gap-1.5 mt-1.5 active:opacity-70" data-testid="m-project-address">
              <MapPin className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <span className="text-sm text-gray-400 truncate">{addrText}</span>
            </button>
          )}
        </div>
        {/* Save Status */}
        {saveStatus !== "idle" && (
          <div className={`ml-2 mt-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1.5 whitespace-nowrap ${
            saveStatus === "saving" ? "bg-blue-500/15 text-blue-400" :
            saveStatus === "saved" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
          }`} data-testid="m-save-indicator">
            {saveStatus === "saving" && <><Loader2 className="w-3 h-3 animate-spin" />Speichern...</>}
            {saveStatus === "saved" && <><Check className="w-3 h-3" />Gespeichert</>}
            {saveStatus === "error" && "Fehler"}
          </div>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div className="px-4 mb-3 flex gap-2">
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white rounded-xl py-3 text-sm font-semibold active:bg-blue-700 transition-colors"
            data-testid="m-project-navigate-btn"
          >
            <Navigation className="w-4 h-4" />Navigation
          </a>
        )}
        <button onClick={() => setShowQuickTask(true)}
          className="flex-1 flex items-center justify-center gap-1.5 bg-amber-500 text-white rounded-xl py-3 text-sm font-bold active:bg-amber-600 transition-colors"
          data-testid="m-quick-task-btn"
        >
          <Zap className="w-4 h-4" />Schnell-Aufgabe
        </button>
      </div>

      {/* ── Unterkunft (synced mit i-Modal — nur wenn dem User zugewiesen + Datum passt) ── */}
      <UserAccommodationCard project={project} userId={user?.id} />

      {/* ── Tab Nav ── */}
      <div className="sticky top-[44px] z-20 bg-[#0f1117]/95 backdrop-blur border-b border-white/10" data-testid="m-project-top-nav">
        <div className="flex overflow-x-auto no-scrollbar px-2">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0
                ${activeTab === tab.id ? "text-blue-400 border-b-2 border-blue-400" : "text-gray-500 hover:text-gray-300 border-b-2 border-transparent"}`}
              data-testid={`m-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.count > 0 && (
                <span className={`ml-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${activeTab === tab.id ? "bg-blue-500/20 text-blue-400" : "bg-white/10 text-gray-500"}`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="px-4 py-4 pb-20">
        {activeTab === "aufgaben" && <MobileTaskList key={taskListKey} projectId={project.id} />}

        {activeTab === "berichte" && (
          <div className="space-y-3">
            <button onClick={onShowBtb}
              className="w-full flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 text-white rounded-xl px-5 py-4 active:bg-white/[0.10] transition-colors"
              data-testid="m-btb-btn"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-blue-400" />
                <div className="text-left"><span className="text-[15px] font-semibold block">Bautagesberichte</span><span className="text-xs text-gray-500">{reportCount} Berichte</span></div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}

        {activeTab === "tresor" && (
          <ProjectVaultTab projectId={project.id} projectName={project.display_name || project.name} isAdmin={isAdmin} />
        )}

        {activeTab === "info" && (
          <div className="space-y-4" data-testid="m-project-info-tab">
            {/* ── Quick-Info Kachel (Einsatzort, Projektleiter, Beschreibung) ── */}
            <ProjectQuickInfoTile
              project={project}
              projectManagerName={projectManagerName}
              currentUserId={user?.id}
              currentUserRole={user?.role}
              onOpenMaps={() => {
                const a = project.address && typeof project.address === "object" ? project.address : null;
                const addrStr = a ? [a.street, [a.zip, a.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : null;
                if (addrStr) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addrStr)}`, "_blank");
              }}
            />

            {/* ── Projektbild (tap to change) ── */}
            {project.project_image ? (
              <div
                className={`relative w-full h-[180px] rounded-xl overflow-hidden border border-white/10 group ${isAdmin ? "cursor-pointer" : ""}`}
                onClick={() => { if (isAdmin) setShowPhotoPicker(true); }}
                data-testid="m-project-image"
              >
                <img src={project.project_image} alt={projectTitle} className="w-full h-full object-cover" />
                {isAdmin && (
                  <div className="absolute inset-0 bg-black/0 group-active:bg-black/40 flex items-center justify-center opacity-0 group-active:opacity-100 transition-all">
                    <div className="bg-black/60 rounded-full px-4 py-2 flex items-center gap-2 text-sm text-white font-medium">
                      <Camera className="w-4 h-4" />Aendern
                    </div>
                  </div>
                )}
              </div>
            ) : isAdmin ? (
              <button
                onClick={() => setShowPhotoPicker(true)}
                className="w-full h-[140px] border-2 border-dashed border-white/15 rounded-xl flex flex-col items-center justify-center gap-2 active:border-blue-400/40 transition-colors"
                data-testid="m-project-add-image"
              >
                <Camera className="w-7 h-7 text-gray-600" />
                <span className="text-xs text-gray-500">Projektbild hinzufuegen</span>
              </button>
            ) : null}

            {/* ── Inline Editable Fields (Admin) ── */}
            {isAdmin ? (
              <div className="space-y-3">
                {/* Projektname */}
                <InlineEditCard label="Projektname" icon={Building2} testId="m-inline-name">
                  <input
                    value={localName}
                    onChange={(e) => { setLocalName(e.target.value); saveDebounced({ name: e.target.value }); }}
                    placeholder="Projektname eingeben"
                    className="w-full bg-transparent text-[15px] font-semibold text-white outline-none border-b border-transparent focus:border-blue-500/50 pb-1 transition-colors placeholder:text-gray-700"
                    data-testid="m-inline-name-input"
                  />
                </InlineEditCard>

                {/* Beschreibung */}
                <InlineEditCard label="Beschreibung" icon={FileText} testId="m-inline-desc">
                  <textarea
                    value={localDesc}
                    onChange={(e) => { setLocalDesc(e.target.value); saveDebounced({ description: e.target.value }); }}
                    placeholder="Beschreibung hinzufuegen..."
                    rows={3}
                    className="w-full bg-transparent text-sm text-gray-300 outline-none border-b border-transparent focus:border-blue-500/50 pb-1 transition-colors resize-none leading-relaxed placeholder:text-gray-700"
                    data-testid="m-inline-desc-input"
                  />
                </InlineEditCard>

                {/* Adresse */}
                <InlineEditCard label="Adresse" icon={MapPin} testId="m-inline-address">
                  <div className="space-y-2">
                    <input
                      value={localStreet}
                      onChange={(e) => {
                        setLocalStreet(e.target.value);
                        saveDebounced({ address: { street: e.target.value, city: localCity, zip: localZip } });
                      }}
                      placeholder="Strasse + Hausnummer"
                      className="w-full bg-transparent text-sm text-gray-300 outline-none border-b border-transparent focus:border-blue-500/50 pb-1 transition-colors placeholder:text-gray-700"
                      data-testid="m-inline-street-input"
                    />
                    <div className="flex gap-3">
                      <input
                        value={localZip}
                        onChange={(e) => {
                          setLocalZip(e.target.value);
                          saveDebounced({ address: { street: localStreet, city: localCity, zip: e.target.value } });
                        }}
                        placeholder="PLZ"
                        className="w-20 bg-transparent text-sm text-gray-300 outline-none border-b border-transparent focus:border-blue-500/50 pb-1 transition-colors placeholder:text-gray-700"
                        data-testid="m-inline-zip-input"
                      />
                      <input
                        value={localCity}
                        onChange={(e) => {
                          setLocalCity(e.target.value);
                          saveDebounced({ address: { street: localStreet, city: e.target.value, zip: localZip } });
                        }}
                        placeholder="Ort"
                        className="flex-1 bg-transparent text-sm text-gray-300 outline-none border-b border-transparent focus:border-blue-500/50 pb-1 transition-colors placeholder:text-gray-700"
                        data-testid="m-inline-city-input"
                      />
                    </div>
                  </div>
                </InlineEditCard>

                {/* Kontakt (read-only) */}
                {(project.contact_person || project.contact_phone) && (
                  <InlineEditCard label="Ansprechpartner" icon={User} testId="m-project-contact">
                    {project.contact_person && <p className="text-sm font-semibold text-white mb-1">{project.contact_person}</p>}
                    {project.contact_phone && (
                      <button onClick={() => callPhone(project.contact_phone)} className="flex items-center gap-2 text-sm font-medium text-blue-400 active:text-blue-300 py-1" data-testid="m-project-phone">
                        <Phone className="w-4 h-4" />{project.contact_phone}
                      </button>
                    )}
                  </InlineEditCard>
                )}
              </div>
            ) : (
              /* Non-admin: read-only */
              <div className="space-y-3">
                {project.description && (
                  <InlineEditCard label="Beschreibung" icon={FileText} testId="m-project-description">
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{project.description}</p>
                  </InlineEditCard>
                )}
                {(project.contact_person || project.contact_phone) && (
                  <InlineEditCard label="Ansprechpartner" icon={User} testId="m-project-contact">
                    {project.contact_person && <p className="text-sm font-semibold text-white mb-1">{project.contact_person}</p>}
                    {project.contact_phone && (
                      <button onClick={() => callPhone(project.contact_phone)} className="flex items-center gap-2 text-sm font-medium text-blue-400 active:text-blue-300 py-1" data-testid="m-project-phone">
                        <Phone className="w-4 h-4" />{project.contact_phone}
                      </button>
                    )}
                  </InlineEditCard>
                )}
              </div>
            )}

            {/* Projekt-Pläne — für alle Projekte */}
            <button onClick={() => navigate(`/app/m/projekte/${project.id}/plaene`)}
              className="w-full flex items-center justify-between bg-white/[0.06] border border-white/10 text-white rounded-xl px-5 py-4 active:bg-white/[0.10] transition-colors"
              data-testid="m-project-plaene-btn"
            >
              <div className="flex items-center gap-3">
                <Map className="w-5 h-5 text-blue-400" />
                <div className="text-left">
                  <span className="text-[15px] font-semibold block">Pläne</span>
                  <span className="text-xs text-gray-500">Grundrisse · Elektro · Sanitär · QR-Sharing</span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </button>

            {/* Projekt Tools (nur Admin / Projektleiter) — bewusst hier statt im Berichte-Tab */}
            {isAdmin && (
              <button onClick={() => navigate(`/app/m/projekt-tools/${project.id}`)}
                className="w-full flex items-center justify-between bg-white/[0.06] border border-white/10 text-white rounded-xl px-5 py-4 active:bg-white/[0.10] transition-colors"
                data-testid="m-project-tools-btn"
              >
                <div className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-blue-400" />
                  <div className="text-left">
                    <span className="text-[15px] font-semibold block">Projekt Tools</span>
                    <span className="text-xs text-gray-500">Torampel · Kabelrechner · Prüfprotokolle</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
        )}
      </div>

      <QuickTaskSheet open={showQuickTask} onClose={() => setShowQuickTask(false)}
        projectId={project.id} userId={user?.id} onCreated={() => setTaskListKey(k => k + 1)} />

      {/* PhotoPicker for project image */}
      <PhotoPicker open={showPhotoPicker} onClose={() => setShowPhotoPicker(false)}
        onCamera={() => { setShowPhotoPicker(false); setTimeout(() => cameraRef.current?.click(), 100); }}
        onGallery={() => { setShowPhotoPicker(false); setTimeout(() => galleryRef.current?.click(), 100); }} />
    </div>
  );
}

/* ── Inline Edit Card (reusable wrapper) ── */
function InlineEditCard({ label, icon: Icon, children, testId }) {
  return (
    <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4 focus-within:border-blue-500/30 transition-colors" data-testid={testId}>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
        <Icon className="w-3 h-3" />{label}
      </label>
      {children}
    </div>
  );
}


/* User-spezifische Unterkunft im Projekt-Detail (synced mit i-Modal) */
const DEFAULT_ACC_VISIBLE = [
  "name", "address", "from_date", "to_date",
  "checkin_from", "checkout_to", "phone", "guest_name",
  "booking_number", "meal_plan",
];
function UserAccommodationCard({ project, userId }) {
  if (!project || !userId) return null;
  const today = new Date().toISOString().slice(0, 10);
  const list = Array.isArray(project.accommodations) ? project.accommodations : [];
  const acc = list.find(a => {
    if (!a) return false;
    const users = Array.isArray(a.assigned_user_ids) ? a.assigned_user_ids : [];
    if (users.length > 0 && !users.includes(userId)) return false;
    const fromOk = !a.from_date || today >= a.from_date;
    const toOk = !a.to_date || today <= a.to_date;
    return fromOk && toOk;
  });
  if (!acc) return null;

  const isApartment = /ferien|wohnung|apartment|fewo/.test((acc.name || "").toLowerCase());
  const Icon = isApartment ? Home : Hotel;
  const fmt = (d) => { if (!d) return ""; try { const [y,m,day] = d.split("-"); return `${day}.${m}.${y}`; } catch { return d; } };
  const mapsUrl = acc.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(acc.address)}` : null;
  const visible = Array.isArray(acc.visible_fields) ? new Set(acc.visible_fields) : new Set(DEFAULT_ACC_VISIBLE);
  const show = (k) => visible.has(k);

  // Wenn Name verborgen wäre, zeigen wir trotzdem mindestens den Namen — eine leere Karte ergibt keinen Sinn
  return (
    <div className="px-4 mb-3" data-testid="m-detail-accommodation">
      <div className={`rounded-xl border p-3 ${isApartment ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-blue-500/25 bg-blue-500/[0.05]"}`}>
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isApartment ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-white font-semibold truncate">{acc.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isApartment ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>
                {isApartment ? "Ferienwohnung" : "Hotel"}
              </span>
            </div>
            {(show("from_date") || show("to_date")) && (acc.from_date || acc.to_date) && (
              <div className="text-[11px] text-gray-400 mt-0.5 font-mono">
                {show("from_date") ? (fmt(acc.from_date) || "—") : "—"} → {show("to_date") ? (fmt(acc.to_date) || "—") : "—"}
              </div>
            )}
            {(show("checkin_from") || show("checkout_to")) && (acc.checkin_from || acc.checkout_to) && (
              <div className="text-[11px] text-gray-400 font-mono">
                {show("checkin_from") && acc.checkin_from && `Check-in ab ${acc.checkin_from}`}
                {show("checkin_from") && acc.checkin_from && show("checkout_to") && acc.checkout_to && " · "}
                {show("checkout_to") && acc.checkout_to && `Check-out bis ${acc.checkout_to}`}
              </div>
            )}
            {show("address") && acc.address && (
              <button
                type="button"
                onClick={() => mapsUrl && window.open(mapsUrl, "_blank")}
                className="flex items-center gap-1 text-[11px] text-gray-400 mt-1 active:opacity-70"
              >
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate underline-offset-2 hover:underline">{acc.address}</span>
              </button>
            )}
            {show("phone") && acc.phone && (
              <a href={`tel:${acc.phone}`} className="flex items-center gap-1 text-[11px] text-blue-300 active:opacity-70">
                <Phone className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{acc.phone}</span>
              </a>
            )}
            {show("guest_name") && acc.guest_name && (
              <div className="text-[11px] text-gray-400">Gast: {acc.guest_name}</div>
            )}
            {show("booking_number") && acc.booking_number && (
              <div className="text-[11px] text-gray-400 font-mono">Buchungsnummer: {acc.booking_number}</div>
            )}
            {show("booking_pin") && acc.booking_pin && (
              <div className="text-[11px] text-gray-400 font-mono">PIN: {acc.booking_pin}</div>
            )}
            {show("meal_plan") && acc.meal_plan && (
              <div className="text-[11px] text-gray-400">Verpflegung: {acc.meal_plan}</div>
            )}
            {((show("room_count") && acc.room_count != null) || (show("nights") && acc.nights != null) || (show("price") && acc.price != null)) && (
              <div className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap">
                {show("room_count") && acc.room_count != null && <span>{acc.room_count} Zimmer</span>}
                {show("nights") && acc.nights != null && <span>{acc.nights} Nächte</span>}
                {show("price") && acc.price != null && <span>{Number(acc.price).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>}
              </div>
            )}
            {show("booking_provider") && acc.booking_provider && (
              <div className="text-[10px] text-gray-500 italic">via {acc.booking_provider}</div>
            )}
            {show("notes") && acc.notes && (
              <div className="text-[11px] text-gray-400 italic mt-1">{acc.notes}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
